'use client';

/**
 * MainSidebar - Universal Navigation Sidebar
 *
 * The standard sidebar navigation for all pages:
 * - Full-height, fills screen edge to edge (no footer beside it)
 * - Collapsed by default for fullscreen/conference layouts
 * - Expanded by default for standard layouts
 * - Consistent color-coded sections
 * - Groups always expanded (no collapse/expand)
 * - Instant tooltips on group headers
 *
 * Uses data-driven navigation from `@/lib/navigation`:
 * - Groups and items fetched from database
 * - Supports agency/department overrides
 * - Icons resolved dynamically from string names
 */

import React from 'react';
import Link from 'next/link';
import { usePathname } from 'next/navigation';
import {
  Loader2,
  CircleSlash,
  Pencil,
} from 'lucide-react';
import {
  useIsAuthenticated,
  useIsStaff,
  useAdminStatus,
} from '../AppShellContext';
import { useSidebarNavigation, resolveIcon } from '@/lib/navigation';
import { isNavGatePassed, type NavAccessState } from '@/lib/navigation/utils';
import { useUserPreference } from '@/hooks/useUserPreferences';
import { getColorScheme, type NavColorScheme, type ColorSchemeConfig } from '@/lib/ui/colorSchemes';
import type { ResolvedNavGroup, ResolvedNavItem } from '@/lib/navigation';
import { InstantTooltip } from '@/components/ui/InstantTooltip';

// ============================================================================
// Types
// ============================================================================

interface MainSidebarProps {
  className?: string;
}

// Group keys that begin a new visual cluster in the sidebar. A faint horizontal
// divider is rendered directly above any group whose key appears here. Keep this
// list in sync with the cluster layout documented in the render loop below.
const CLUSTER_START_KEYS: ReadonlySet<string> = new Set([
  'calls',   // starts "services" cluster (after home)
  'reports', // starts "results & data" cluster (after training)
  'admin',   // starts "settings" cluster (after library)
]);

// ============================================================================
// Tooltip content helper
// ============================================================================

function GroupTooltipContent({ label, description }: { label: string; description?: string }) {
  return (
    <div>
      <div className="font-medium text-white">{label}</div>
      {description && <div className="text-xs text-white/70 mt-0.5">{description}</div>}
    </div>
  );
}

// ============================================================================
// Color Helper - Uses shared color schemes
// ============================================================================

function getColorConfig(colorScheme: NavColorScheme | undefined) {
  return getColorScheme(colorScheme, 'blue');
}

function toGroupHoverBg(bgClass: string) {
  return bgClass.replace(/\bbg-/g, 'group-hover:bg-');
}

// ============================================================================
// Component
// ============================================================================

/**
 * Recursively filter a nav item's children by access. Returns null if
 * the item itself is gated out, otherwise returns a copy with children
 * filtered. Defense-in-depth — server filter is authoritative.
 */
function filterItemByAccess(
  item: ResolvedNavItem,
  access: NavAccessState,
): ResolvedNavItem | null {
  if (!isNavGatePassed(item, access)) return null;

  if (!item.children || item.children.length === 0) return item;

  const filteredChildren = item.children
    .map((child) => filterItemByAccess(child, access))
    .filter((child): child is ResolvedNavItem => child !== null);

  return { ...item, children: filteredChildren };
}

export function MainSidebar({
  className = '',
}: MainSidebarProps) {
  const collapsed = true; // Always collapsed — icon-only sidebar
  const pathname = usePathname();
  const isAuthenticated = useIsAuthenticated();
  const isStaff = useIsStaff();
  const adminStatus = useAdminStatus();
  // Use the data-driven navigation hook
  const {
    groups,
    isLoading,
    error,
    refresh,
  } = useSidebarNavigation();

  // Defense-in-depth client-side ACL: hide groups/items the current user
  // shouldn't see (e.g. admin-only sections for non-admin staff). The
  // server filter in /api/navigation is authoritative — this prevents any
  // entries that slip past (cached payloads, child items under a parent
  // that itself passes the role check, etc.) from being visible.
  // See issue #247.
  const access: NavAccessState = {
    isAuthenticated,
    isStaff,
    isAdmin: adminStatus.isAdmin,
    isPlatformAdmin: adminStatus.isPlatformAdmin,
  };

  // Compute visibleGroups BEFORE any early-return so hook count stays stable
  // across renders where isStaff flips (auth loading → loaded). Calling
  // React.useMemo conditionally violates the rules of hooks and causes
  // React error #310 on the first render after auth resolves.
  const visibleGroups = React.useMemo(() => {
    return groups
      .map((group) => {
        if (!isNavGatePassed(group, access)) return null;
        const filteredItems = group.items
          .map((item) => filterItemByAccess(item, access))
          .filter((item): item is ResolvedNavItem => item !== null);
        // If a group has no remaining items AND no direct primaryPath, hide it
        if (filteredItems.length === 0 && !group.primaryPath) return null;
        return { ...group, items: filteredItems };
      })
      .filter((group): group is ResolvedNavGroup => group !== null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [groups, access.isAuthenticated, access.isStaff, access.isAdmin, access.isPlatformAdmin]);

  // Only show sidebar for staff users (early return AFTER all hooks)
  if (!isStaff) {
    return null;
  }

  return (
    <aside
      data-demo-id="main-sidebar"
      className={`hidden md:flex flex-col border-r border-white/[0.08] w-14 h-full ${className}`}
      style={{ backgroundColor: '#0a0a0a' }}
    >
      {/* Navigation Groups */}
      <nav className={`flex-1 py-0.5 ${collapsed ? 'overflow-visible pointer-events-none' : 'overflow-y-auto'}`}>
        {isLoading ? (
          <div className="flex items-center justify-center py-8">
            <Loader2 className="w-5 h-5 animate-spin text-white/70" />
          </div>
        ) : error ? (
          <div className="flex flex-col items-center justify-center py-8 px-4 text-center">
            <CircleSlash className="w-5 h-5 text-blue-400/60 mb-2" />
            <span className="text-xs text-white/70 mb-2">Failed to load navigation</span>
            <button
              onClick={() => void refresh()}
              className="text-[10px] text-blue-400 hover:text-blue-300 underline cursor-pointer"
            >
              Retry
            </button>
          </div>
        ) : visibleGroups.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-8 px-4 text-center">
            <span className="text-xs text-white/70">No navigation items</span>
          </div>
        ) : (
          visibleGroups.map((group, index) => {
            // Show a divider BEFORE this group only when it starts a new cluster.
            // Clusters (top-to-bottom):
            //   1. home                           — {home}
            //   2. services                       — {calls, training}
            //   3. results & data                 — {reports, analytics, library}
            //   4. settings                       — {admin, settings}
            // So dividers appear before calls, reports, and admin (not between
            // groups inside the same cluster).
            const showDividerBefore = index > 0 && CLUSTER_START_KEYS.has(group.key);

            return (
              <React.Fragment key={group.key}>
                {showDividerBefore && (
                  <div className="my-2 mx-3 border-t border-white/[0.15]" />
                )}
                {/* Leaf mode: groups with primaryPath navigate directly instead of expanding */}
                {group.primaryPath ? (
                  <SidebarLeafGroup group={group} collapsed={collapsed} pathname={pathname} />
                ) : (
                  <SidebarGroup
                    group={group}
                    collapsed={collapsed}
                  />
                )}
              </React.Fragment>
            );
          })
        )}
      </nav>

    </aside>
  );
}

// ============================================================================
// SidebarLeafGroup Component — Direct navigation (e.g. Home → /dashboard)
// ============================================================================

interface SidebarLeafGroupProps {
  group: ResolvedNavGroup;
  collapsed: boolean;
  pathname: string;
}

function SidebarLeafGroup({ group, collapsed, pathname }: SidebarLeafGroupProps) {
  const colors = getColorConfig(group.colorScheme);
  const GroupIcon = resolveIcon(group.iconName) || CircleSlash;
  const isHome = group.key === 'home';

  // Custom home page destination — users can pick their preferred landing page
  const [homePagePath] = useUserPreference<string>('home_page_path', '/dashboard');
  const effectivePath = isHome ? (homePagePath || group.primaryPath!) : group.primaryPath!;
  const isActive = pathname === effectivePath || (isHome && pathname === '/dashboard');
  const collapsedHoverIconBg = toGroupHoverBg(colors.iconBg);
  const collapsedIconState = isActive ? colors.iconColor : 'text-white/70';
  const collapsedOuterState = isActive
    ? 'bg-white/[0.06] ring-1 ring-white/[0.06]'
    : 'hover:bg-white/[0.06]';
  const collapsedInnerState = isActive ? 'bg-white/[0.08]' : 'bg-white/[0.04]';

  // Edit badge for home button — links to settings to change home page.
  // Visible chrome stays at 16×16 to preserve layout, but a transparent
  // ::before pseudo-element expands the click target to 24×24 so the
  // interactive surface meets WCAG 2.5.8 (Target Size, Minimum, Level AA).
  const editBadge = isHome && (
    <Link
      href="/settings"
      prefetch={false}
      onClick={(e) => e.stopPropagation()}
      aria-label="Change home page"
      className="absolute -bottom-0.5 -right-0.5 w-4 h-4 rounded-full bg-slate-700 border border-slate-600 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity duration-200 hover:bg-blue-600 hover:border-blue-500 z-10 before:absolute before:content-[''] before:-inset-1 before:rounded-full"
      title="Change home page"
    >
      <Pencil className="w-2 h-2 text-white" aria-hidden="true" />
    </Link>
  );

  if (collapsed) {
    return (
      <div className="mb-0 px-1 pointer-events-auto">
        <InstantTooltip
          position="right"
          content={<GroupTooltipContent label={group.label} description={group.description} />}
        >
          <Link
            href={effectivePath}
            prefetch={false}
            aria-label={group.label}
            className="flex items-center justify-center group"
          >
            <div
              className={`
                relative flex items-center justify-center w-11 h-11 rounded-xl transition-all duration-200
                ${collapsedOuterState}
              `}
            >
              <div className={`
                p-0.5 rounded-lg transition-all duration-200 ${collapsedInnerState} ${collapsedHoverIconBg} group-hover:scale-105
              `}>
                <GroupIcon className={`w-7 h-7 transition-colors duration-200 ${collapsedIconState} ${colors.hoverText}`} aria-hidden="true" />
              </div>
              {editBadge}
            </div>
          </Link>
        </InstantTooltip>
      </div>
    );
  }

  // Expanded mode — single clickable row
  return (
    <div className="px-2">
      <InstantTooltip
        position="right"
        content={<GroupTooltipContent label={group.label} description={group.description} />}
      >
        <Link
          href={effectivePath}
          prefetch={false}
          data-demo-id={`sidebar-group-${group.key}`}
          className={`
            relative flex items-center gap-2 px-2 py-1.5 rounded-lg text-sm transition-all duration-200 group
            ${isActive ? colors.activeBg : `hover:bg-white/[0.04] ${colors.hoverBg}`}
          `}
        >
          <div className={`
            relative p-1 rounded-md transition-all duration-200
            ${isActive ? colors.iconBg : 'bg-white/[0.04]'}
          `}>
            <GroupIcon className={`w-5 h-5 ${colors.iconColor}`} />
            {editBadge}
          </div>
          <span className={`truncate transition-colors duration-200 ${isActive ? colors.activeText : `text-white/60 ${colors.hoverText}`}`}>
            {group.label}
          </span>
          {isActive && (
            <div className={`ml-auto w-1.5 h-1.5 rounded-full ${colors.dot}`} />
          )}
        </Link>
      </InstantTooltip>
    </div>
  );
}

// ============================================================================
// SidebarGroup Component — Always expanded, no collapse toggle
// ============================================================================

interface SidebarGroupProps {
  group: ResolvedNavGroup;
  collapsed: boolean;
}

function SidebarGroup({ group, collapsed }: SidebarGroupProps) {
  const colors = getColorConfig(group.colorScheme);
  const GroupIcon = resolveIcon(group.iconName) || CircleSlash;

  const hasActiveItem = group.items.some(
    item => item.isActive || item.children?.some(child => child.isActive)
  );
  const collapsedHoverIconBg = toGroupHoverBg(colors.iconBg);
  const collapsedOuterState = hasActiveItem
    ? 'bg-white/[0.06] ring-1 ring-white/[0.06]'
    : 'hover:bg-white/[0.06]';
  const collapsedInnerState = hasActiveItem ? 'bg-white/[0.08]' : 'bg-white/[0.04]';
  const collapsedIconState = hasActiveItem ? colors.iconColor : 'text-white/70';

  // Collapsed mode - show only group icon, click navigates
  if (collapsed) {
    const firstItemPath = group.items[0]?.path || '/';
    return (
      <div className="relative mb-0 px-1 pointer-events-auto">
        <InstantTooltip
          position="right"
          content={<GroupTooltipContent label={group.label} description={group.description} />}
        >
          <Link
            href={firstItemPath}
            prefetch={false}
            aria-label={group.label}
            className="flex items-center justify-center group"
          >
            <div
              className={`
                flex items-center justify-center w-11 h-11 rounded-xl transition-all duration-200 cursor-pointer flex-shrink-0
                ${collapsedOuterState}
              `}
            >
              <div className={`
                p-0.5 rounded-lg transition-all duration-200 ${collapsedInnerState} ${collapsedHoverIconBg} group-hover:scale-105
              `}>
                <GroupIcon className={`w-7 h-7 transition-colors duration-200 ${collapsedIconState} ${colors.hoverText}`} aria-hidden="true" />
              </div>
            </div>
          </Link>
        </InstantTooltip>
      </div>
    );
  }

  // Expanded mode - always show group header and items (no collapse)
  return (
    <div>
      {/* Group Header — static label with tooltip */}
      <InstantTooltip
        position="right"
        content={<GroupTooltipContent label={group.label} description={group.description} />}
      >
        <div
          data-demo-id={`sidebar-group-${group.key}`}
          data-testid={group.key === 'library' ? 'sidebar-section-library' : undefined}
          className="flex items-center px-3 py-1 text-[10px] font-semibold text-white/70 uppercase tracking-wider"
        >
          <div className="flex items-center gap-1.5">
            <div className={`w-1 h-1 rounded-full ${colors.dot} opacity-60`} />
            <span>{group.label}</span>
          </div>
        </div>
      </InstantTooltip>

      {/* Group Items — always visible */}
      <div className="px-1.5 pb-0.5">
        {group.items.map((item) => (
          <SidebarItem
            key={item.key}
            item={item}
            colors={colors}
            depth={0}
          />
        ))}
      </div>
    </div>
  );
}

// ============================================================================
// SidebarItem Component - Always shows children (no collapse)
// ============================================================================

interface SidebarItemProps {
  item: ResolvedNavItem;
  colors: ColorSchemeConfig;
  depth: number;
}

function SidebarItem({ item, colors, depth }: SidebarItemProps) {
  const Icon = resolveIcon(item.iconName) || CircleSlash;
  const hasChildren = item.children && item.children.length > 0;
  const isActive = item.isActive;
  const hasActiveChild = hasChildren && item.children!.some(child => child.isActive);

  // For items with children: show parent + always-visible children
  if (hasChildren && depth < 2) {
    return (
      <div>
        {/* Parent item row */}
        <Link
          href={item.path}
          prefetch={false}
          target={item.external ? '_blank' : undefined}
          rel={item.external ? 'noopener noreferrer' : undefined}
          data-demo-id={`sidebar-${item.key}`}
          className={`
            flex items-center gap-2 px-2 py-1.5 rounded-lg text-sm transition-all duration-200 border-l-2 border-transparent group
            ${depth > 0 ? 'pl-5' : ''}
            ${isActive ? `${colors.activeBg} ${colors.activeBorder}` : hasActiveChild ? `${colors.activeBg} border-transparent` : `hover:bg-white/[0.04] ${colors.hoverBg}`}
          `}
        >
          <div className={`p-1 rounded-md ${colors.iconBg} group-hover:scale-105 transition-all duration-200`}>
            <Icon className={`w-5 h-5 flex-shrink-0 ${colors.iconColor}`} />
          </div>
          <span className={`truncate transition-colors duration-200 ${isActive || hasActiveChild ? colors.activeText : `text-white/60 ${colors.hoverText}`}`}>
            {item.label}
          </span>
          {item.badge && (
            <span className={`
              ml-auto px-1.5 py-0.5 text-[10px] font-medium rounded flex-shrink-0
              ${item.badge.color || 'bg-white/10 text-white/60'}
            `}>
              {item.badge.value}
            </span>
          )}
        </Link>

        {/* Children — always visible */}
        <div className={`ml-3 pl-1.5 border-l border-white/[0.06] ${item.children!.length > 12 ? 'max-h-64 overflow-y-auto' : ''}`}>
          {item.children!.map((child) => (
            <SidebarItem
              key={child.key}
              item={child}
              colors={colors}
              depth={depth + 1}
            />
          ))}
        </div>
      </div>
    );
  }

  // Leaf item (no children) or max depth reached
  return (
    <Link
      href={item.path}
      prefetch={false}
      target={item.external ? '_blank' : undefined}
      rel={item.external ? 'noopener noreferrer' : undefined}
      data-demo-id={`sidebar-${item.key}`}
      data-testid={item.path === '/knowledge-base' ? 'sidebar-link-knowledge-base' : undefined}
      className={`
        flex items-center gap-2 px-2 py-1.5 rounded-lg text-sm transition-all duration-200 w-full border-l-2 border-transparent group
        ${depth > 0 ? 'py-1 text-xs' : ''}
        ${isActive ? `${colors.activeBg} ${colors.activeBorder}` : `hover:bg-white/[0.04] ${colors.hoverBg}`}
      `}
    >
      {/* Icon with background */}
      {depth === 0 ? (
        <div className={`p-1 rounded-md ${colors.iconBg} group-hover:scale-105 transition-all duration-200`}>
          <Icon className={`w-5 h-5 flex-shrink-0 ${colors.iconColor}`} />
        </div>
      ) : (
        <Icon className={`w-4 h-4 flex-shrink-0 ${isActive ? colors.iconColor : 'text-white/70'}`} />
      )}
      {/* Text */}
      <span className={`truncate transition-colors duration-200 ${isActive ? colors.activeText : `text-white/60 ${colors.hoverText}`}`}>
        {item.label}
      </span>
      {/* Badge display */}
      {item.badge && (
        <span className={`
          ml-auto px-1.5 py-0.5 text-[10px] font-medium rounded flex-shrink-0
          ${item.badge.color || 'bg-white/10 text-white/60'}
        `}>
          {item.badge.value}
        </span>
      )}
      {/* Active dot for depth > 0 */}
      {isActive && depth > 0 && !item.badge && (
        <div className={`ml-auto w-1 h-1 rounded-full ${colors.dot}`} />
      )}
    </Link>
  );
}

// ============================================================================
// Export
// ============================================================================

export default MainSidebar;
